import * as React from 'react'
import { PlatformMessageDto } from '@halo/dtos/platform-message.dto'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc'
import { MessageTableDataDto } from '@halo/dtos/platform-message-table-data-dto'
import { Button } from '@pushly/aqe/lib/components'
import { CopyOutlined, DeleteFilled, EditOutlined, Loading3QuartersOutlined } from '@ant-design/icons'
import { Table, Dropdown, Menu, Modal, notification } from 'antd'
import * as moment from 'moment'
import { PlatformMessageEditor } from '@halo/components/platform-message-form/platform-message-form'
import { classnames } from '@halo/_utils/classnames'
import randomstring from 'randomstring'
import './platform-messages.scss'
import deepEqual from 'react-fast-compare'
import { ContentState, convertFromHTML, EditorState } from 'draft-js'
import { simpleNotification } from '@halo/_utils/notifications'
import DataTableView from '@halo/components/data-table-view/data-table-view'
import { DataTableContext } from '../../components/data-table-view/context'
import EntityDisplay, { EntityTitle } from '@halo/components/data-table-view/entity-display'
import titleCase from 'title-case'
import { AntdTableEmptyPlaceholder } from '@halo/components/antd-table-empty-placeholder/antd-table-empty-placeholder'
import { fmtDateString } from '@halo/helpers/formatting'
import { noop } from '@pushly/aqe/lib/utils/noop'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'

interface IPlatformMessageList {}

interface IPMLState {
    loading: boolean
    allMessages: PlatformMessageDto[]
    visibleMessages: PlatformMessageDto[]
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    tagFilter?: any
    showMessageEditor: boolean
    drawerKey?: string
    messageToEdit?: any
}

export class PlatformMessageList extends React.Component<IPlatformMessageList, IPMLState> {
    protected appState: AppState
    protected appService: AppService

    public constructor(props: any) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)

        this.state = {
            loading: true,
            allMessages: [],
            visibleMessages: [],
            page: 1,
            pageSize: 15,
            showMessageEditor: false,
            tagFilter: [],
        }
    }

    public componentDidMount(): void {
        this.fetchMessages()
        this.fetchFilters()
    }

    public render() {
        const pagination: any = {
            pageSize: this.state.pageSize,
            total: this.state.visibleMessages.length - 1,
            current: this.state.page,
            showTotal: (total: number, range: number[]) => `Showing ${range[0]}-${range[1]} of ${total} Messages`,
            size: 'small',
            showSizeChanger: false,
            showSizeOptions: ['10', '25', '50'],
            onChange: async (page: number, pageSize: number) => {
                await this.setState({ page })
            },
            itemRender: (current: any, type: string, originalElement: any) => {
                switch (type) {
                    case 'prev':
                        return <a>Previous</a>
                    case 'next':
                        return <a>Next</a>
                    case 'page':
                        return ''
                }

                return originalElement
            },
        }

        return (
            <div className={classnames('platform-message-list')}>
                <DataTableContext.Provider
                    value={{
                        loading: this.state.loading,
                        currentDataSource: this.state.visibleMessages,
                        resolveDataSource: this.resolveMessages,
                    }}
                >
                    <DataTableView
                        searchPlaceholder="Search message ID or title"
                        filtersDefaultValue={this.state.tagFilter}
                        filters={this.fetchFilters()}
                        onFiltersChange={(filterTag) => this.filterMessageTags(filterTag)}
                        onSearchChange={(v) => this.searchMessages(v)}
                        actions={[
                            <Button key="new-message" size="small" type="primary" onClick={this.openDrawer}>
                                New Message
                            </Button>,
                        ]}
                    >
                        <Table
                            size="small"
                            bordered={false}
                            // scroll.y used to freeze header row
                            // scroll.x used to fix bug with col width and scroll.y
                            // https://github.com/ant-design/ant-design/issues/7603#issuecomment-697600114
                            scroll={{ y: 555, x: 'max-content' }}
                            dataSource={this.state.visibleMessages}
                            loading={
                                this.state.loading && {
                                    indicator: <Loading3QuartersOutlined spin={true} />,
                                }
                            }
                            // onChange={this.fetchMessages}
                            pagination={false}
                            footer={(currentTableData) => (
                                <SwSimpleLinkPagination
                                    currentTableData={currentTableData}
                                    links={this.state.paginationLinks}
                                    onPrev={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchMessages())
                                    }}
                                    onNext={(_ev, _link, config) => {
                                        this.setState({ page: config.current! }, () => this.fetchMessages())
                                    }}
                                />
                            )}
                            locale={{
                                emptyText: <AntdTableEmptyPlaceholder text="No Messages" icon="message" />,
                            }}
                        >
                            <Table.Column
                                key="message"
                                title="Message"
                                render={(msg: any) => {
                                    const { title, message, id } = msg
                                    return (
                                        <EntityDisplay
                                            top={{
                                                left: <EntityTitle>{title}</EntityTitle>,
                                            }}
                                            mid={{
                                                left: <div dangerouslySetInnerHTML={{ __html: message }} />,
                                            }}
                                            bottom={{
                                                left: <span className="message-id">#{id}</span>,
                                            }}
                                        />
                                    )
                                }}
                                className="message"
                            />
                            <Table.Column
                                key="delivery"
                                title="Delivery"
                                render={this.renderDelivery}
                                className="delivery"
                            />
                            <Table.Column
                                key="actions"
                                title="Actions"
                                align="center"
                                className="actions"
                                render={(_, msg: any) => {
                                    return (
                                        <Dropdown.Button
                                            size="small"
                                            className="platform-message-list-row-actions-dropdown-menu"
                                            onClick={() => this.deleteMessage(msg)}
                                            overlay={
                                                <Menu className="">
                                                    {(moment(msg.end_time) > moment() || msg.end_time === null) && (
                                                        <Menu.Item
                                                            key="EDIT"
                                                            onClick={() => this.handleEditMessage(msg)}
                                                        >
                                                            <EditOutlined />
                                                            <span>Edit</span>
                                                        </Menu.Item>
                                                    )}
                                                    <Menu.Item
                                                        key="DUPLICATE"
                                                        onClick={() => this.handleDuplicateMsg(msg)}
                                                    >
                                                        <CopyOutlined />
                                                        <span>Duplicate</span>
                                                    </Menu.Item>
                                                </Menu>
                                            }
                                        >
                                            <DeleteFilled />
                                            <span>Delete</span>
                                        </Dropdown.Button>
                                    )
                                }}
                            />
                        </Table>
                    </DataTableView>
                </DataTableContext.Provider>

                <PlatformMessageEditor
                    key={this.state.drawerKey}
                    visible={this.state.showMessageEditor}
                    onClose={this.handleDrawerClose}
                    allMessages={this.state.visibleMessages}
                    message={this.state.messageToEdit && this.configMessageToEdit()}
                    onMessageUpdated={this.onMessageUpdate}
                />
            </div>
        )
    }

    protected resolveMessages = () => {
        const query = this.state.query
        if (query?.length > 2 || query === undefined) {
            return this.fetchMessages()
        }
    }

    protected searchMessages = async (value: string): Promise<void> => {
        let query = value.trim()

        if (value.length < 2) {
            query = undefined
        }

        this.setState({ query })

        // return this.fetchMessages();
    }

    protected filterMessageTags = async (tagFilter: any) => {
        if (!deepEqual(tagFilter, this.state.tagFilter)) {
            tagFilter = tagFilter || []
            this.setState({ tagFilter })

            return this.fetchMessages()
        }
    }

    protected onMessageUpdate = () => {
        this.handleDrawerClose()
        this.fetchMessages()
    }

    protected openDrawer = async (): Promise<void> => {
        return this.setState({ showMessageEditor: true, drawerKey: randomstring.generate() })
    }

    protected handleDrawerClose = async () => {
        return this.setState({ showMessageEditor: false, messageToEdit: undefined })
    }

    protected deleteMessage = async (msg: any) => {
        Modal.confirm({
            okText: 'Delete',
            okButtonProps: {
                size: 'small',
            },
            cancelButtonProps: {
                size: 'small',
            },
            title: 'Delete Message',
            content: (
                <>
                    <p>Are you sure you want to (soft) delete the following message?</p>
                    <p>
                        <b>Title:</b> {msg.title}
                        <br />
                        <b>ID:</b> {msg.id}
                        <br />
                    </p>
                </>
            ),
            onOk: async (...args: any[]) => {
                const res = await this.appService.destroyMessage(msg)

                if (res) {
                    notification.success({
                        message: '',
                        description: 'Schedule successfully deleted.',
                    })

                    this.fetchMessages()
                } else {
                    console.warn(res)
                    this.fetchMessages()

                    return notification.error({
                        message: '',
                        description: 'The message is unable to be updated at this time. See console for more details,',
                    })
                }
            },
            onCancel: noop,
        })
    }

    protected handleEditMessage = async (msg: any) => {
        this.setState({
            drawerKey: randomstring.generate(),
            messageToEdit: msg,
            showMessageEditor: true,
        })
    }

    protected handleDuplicateMsg = async (msg: any) => {
        const duplicateMessage: any = {
            title: msg.title,
            message: msg.message,
            start_time: msg.start_time,
            end_time: msg.end_time,
        }

        if (msg.domainIds) {
            duplicateMessage.domain_ids = msg.domain_ids
        }
        if (msg.minVersionReq) {
            duplicateMessage.min_version_req = msg.min_version_req
        }
        if (msg.maintenanceMode) {
            duplicateMessage.maintenance_mode = msg.maintenance_mode
        }

        this.setState({
            drawerKey: randomstring.generate(),
            messageToEdit: duplicateMessage,
            showMessageEditor: true,
        })
    }

    protected renderMessage(text: string, msg: MessageTableDataDto): React.ReactNode {
        return (
            <EntityDisplay
                top={{
                    left: <EntityTitle>{msg.title}</EntityTitle>,
                }}
                mid={{
                    left: <div dangerouslySetInnerHTML={{ __html: msg.message }} />,
                }}
            />
        )
    }

    protected renderDelivery(text: string, msg: any): React.ReactNode {
        return !msg.start_time ? (
            <>--</>
        ) : (
            <EntityDisplay
                top={{ left: <span>{msg.start_time ? fmtDateString(msg.start_time, 'M/D LT') : 'Now'} -</span> }}
                mid={{ left: <span>{msg.end_time ? fmtDateString(msg.end_time, 'M/D LT') : 'On-Going'}</span> }}
            />
        )
    }

    protected configMessageToEdit() {
        const { messageToEdit } = this.state
        let message: any = {}
        message = Object.assign(message, messageToEdit)

        if (message.message) {
            const content = convertFromHTML(message.message)
            message.message = EditorState.createWithContent(
                ContentState.createFromBlockArray(content.contentBlocks, content.entityMap),
            )
        }

        return message
    }

    protected fetchFilters = async (): Promise<any[]> => {
        try {
            const filters: any = {
                className: 'tag-filter',
                dataIndex: 'tag-filter',
                multiple: true,
                maxTagCount: 1,
                placeholder: 'All tags',
                optionPrefix: 'Tags',
                onClose: (tag: any) => this.filterMessageTags(tag),
            }

            const filtersOpts = await this.appService.getFilterOpts()

            if (!!filtersOpts) {
                filters.options = filtersOpts.map((tag: string) => ({
                    group: 'Tags',
                    label: titleCase(tag),
                    value: tag,
                }))

                return [filters]
            }
        } catch (error) {
            simpleNotification('error', error)
            return []
        }
    }

    protected fetchMessages = async (): Promise<void> => {
        await this.setState({ loading: true })
        const options = {
            showLoadingScreen: false,
            query: {
                search: this.state.query,
                tags: this.state.tagFilter['tag-filter'],
            },
        }

        try {
            const { ok, data, meta, cancelled, error } = await this.appService.fetchPlatformMessages(options)
            if (ok) {
                const messageDtos = data.map((msg: PlatformMessageDto) => {
                    return MessageTableDataDto.fromMessageDto(msg)
                })

                await this.setState((currState) => ({
                    loading: false,
                    allMessages: messageDtos,
                    visibleMessages: messageDtos,
                    paginationLinks: meta.links ?? currState.paginationLinks,
                }))
            }
        } catch (error) {
            simpleNotification('error', error)
        }

        this.appService.unsetModuleLoading()
    }
}
